import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCamera, FiCheckCircle, FiSearch, FiX } from "react-icons/fi";
import "./TrackingCapture.css";
import { elakayAutoText } from "./dataElakay";


export const trackingFromBarcode = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const url = new URL(raw);
    const keys = [
      "tracking",
      "trackingNumber",
      "trackingnumber",
      "tracknum",
      "trknbr",
      "tLabels",
      "number",
      "pin"
    ];

    for (const key of keys) {
      const tracking = url.searchParams.get(key);
      if (tracking) return tracking.trim();
    }

    const hashTracking = url.hash.match(/details\/([^/?#]+)/i);
    if (hashTracking?.[1]) return decodeURIComponent(hashTracking[1]);
  } catch {
    // Most package barcodes contain the tracking number directly.
  }

  return raw;
};

export const normalizeTracking = (value) => (
  trackingFromBarcode(value).toLowerCase().replace(/[\s-]+/g, "")
);

const TrackingCapture = ({
  value,
  onChange,
  onSubmit,
  loading = false,
  disabled = false,
  autoSubmitOnScan = true,
  showSubmit = true,
  title = "Tracking de la commande",
  placeholder = "Scanner ou saisir le tracking...",
  submitLabel = "Charger la commande",
  compact = false,
  message = "",
  error = ""
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const detectingRef = useRef(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");

  const stopScanner = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    detectingRef.current = false;
  }, []);

  const acceptTracking = useCallback(async (rawValue, submit = false) => {
    const tracking = trackingFromBarcode(rawValue);
    if (!tracking) return;
    onChange?.(tracking);
    if (submit) await onSubmit?.(tracking, { source: "scanner" });
  }, [onChange, onSubmit]);

  useEffect(() => {
    if (!scannerOpen) {
      stopScanner();
      return undefined;
    }

    let cancelled = false;

    const startScanner = async () => {
      setScannerError("");
      if (!navigator.mediaDevices?.getUserMedia || !window.BarcodeDetector) {
        setScannerError(elakayAutoText("h_pay_camera_unavailable", "Camera scanner unavailable. Enter tracking manually."));
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        const detector = new window.BarcodeDetector();

        timerRef.current = setInterval(async () => {
          if (!videoRef.current || detectingRef.current) return;
          detectingRef.current = true;
          try {
            const results = await detector.detect(videoRef.current);
            if (results?.[0]?.rawValue) {
              stopScanner();
              setScannerOpen(false);
              await acceptTracking(results[0].rawValue, autoSubmitOnScan);
            }
          } catch {
            // Continue with the next camera frame.
          } finally {
            detectingRef.current = false;
          }
        }, 650);
      } catch {
        setScannerError(elakayAutoText("h_pay_camera_denied", "Camera denied or unavailable. Check permissions or enter tracking manually."));
      }
    };

    startScanner();
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [acceptTracking, autoSubmitOnScan, scannerOpen, stopScanner]);

  useEffect(() => () => stopScanner(), [stopScanner]);

  const submit = async (event) => {
    event.preventDefault();
    const tracking = trackingFromBarcode(value);
    if (!tracking || loading || disabled) return;
    onChange?.(tracking);
    await onSubmit?.(tracking, { source: "manual" });
  };

  return (
    <section className={`tracking-capture${compact ? " tracking-capture-compact" : ""}`}>
      <div className="tracking-capture-heading">
        <div>
          <strong>{title}</strong>
          <span>{elakayAutoText("h_c28587f456216b", "Saisie manuelle, lecteur USB/Bluetooth ou camera")}</span>
        </div>
        <button
          type="button"
          className="tracking-capture-scan"
          onClick={() => setScannerOpen(true)}
          disabled={disabled || loading}
        >
          <FiCamera />{elakayAutoText("h_c7ee483aa3d721", "Scanner")}</button>
      </div>

      <form className="tracking-capture-form" onSubmit={submit}>
        <div className="tracking-capture-input">
          <FiSearch />
          <input
            type="text"
            value={value}
            onChange={(event) => onChange?.(event.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            disabled={disabled}
          />
        </div>
        {showSubmit && (
          <button type="submit" disabled={disabled || loading || !String(value || "").trim()}>
            {loading ? elakayAutoText("h_a209b664efa2dc", "Chargement...") : submitLabel}
          </button>
        )}
      </form>

      {error && <div className="tracking-capture-alert is-error">{error}</div>}
      {message && (
        <div className="tracking-capture-alert is-success">
          <FiCheckCircle /> {message}
        </div>
      )}

      {scannerOpen && (
        <div className="tracking-capture-camera">
          <div className="tracking-capture-stage">
            <video ref={videoRef} muted playsInline />
            {!scannerError && <div className="tracking-capture-frame"><span /></div>}
          </div>
          <div className="tracking-capture-camera-footer">
            <span>{scannerError || elakayAutoText("h_5ee1b11f67f1dc", "Cadrez le code-barres du tracking.")}</span>
            <button type="button" onClick={() => setScannerOpen(false)}>
              <FiX />{elakayAutoText("h_5ab4ec640d654d", "Fermer")}</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default TrackingCapture;
